import React from 'react';
import {useIntl} from 'react-intl';
import {MCP_TOOL_KIND_ORDER, McpToolKind, NovaMcpTool, mcpToolToken} from '../shared/novaMcpTools';
import {NovaMcpToolsState} from '../shared/useNovaMcpTools';
import {InsertLibraryGroup, NovaInsertLibrary} from './NovaInsertLibrary';

const GROUP_LABEL_ID: Record<McpToolKind, string> = {
	[McpToolKind.Read]: 'nova.mcpTools.groupRead',
	[McpToolKind.Preview]: 'nova.mcpTools.groupPreview',
	[McpToolKind.Write]: 'nova.mcpTools.groupWrite',
};

interface Props {
	onInsert: (token: string) => void;
	// The tools Forecast-MCP serves, and whether the list has loaded.
	mcpTools: NovaMcpToolsState;
	// The prompt a click inserts into, shown as a pill in the header.
	targetLabel?: string;
	// Reads the list again after a failed read.
	onRetry?: () => void;
}

// Every tool an agent can call, grouped by whether it reads, previews or writes. Clicking one drops
// its `{tool:name}` reference at the caret of whichever prompt is the target; a reference is not data,
// so every tool is insertable in every prompt.
export function NovaMcpTools({onInsert, mcpTools, targetLabel, onRetry}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const badgesOf = (tool: NovaMcpTool): string[] => [
		...(tool.plannerOnly ? [formatMessage({id: 'nova.mcpTools.plannerOnly'})] : []),
		...(tool.featureFlag ? [formatMessage({id: 'nova.mcpTools.featureFlag'}, {flag: tool.featureFlag})] : []),
	];

	const groups: InsertLibraryGroup[] =
		mcpTools.status === 'loaded'
			? MCP_TOOL_KIND_ORDER.map(kind => ({
					key: kind.toLowerCase(),
					label: formatMessage({id: GROUP_LABEL_ID[kind]}),
					rows: mcpTools.tools
						.filter(tool => tool.kind === kind)
						.map(tool => ({
							key: tool.name,
							token: tool.name,
							description: tool.purpose,
							secondary: formatMessage({id: 'nova.mcpTools.module'}, {module: tool.module}),
							badges: badgesOf(tool),
							dataCy: `nova-mcp-tool-${tool.name}`,
							onInsert: () => onInsert(mcpToolToken(tool)),
						})),
			  }))
			: [];
	const countLabel = {
		loading: () => formatMessage({id: 'nova.mcpTools.loading'}),
		failed: () => formatMessage({id: 'nova.mcpTools.countUnavailable'}),
		loaded: () => formatMessage({id: 'nova.mcpTools.count'}, {count: mcpTools.tools.length}),
	}[mcpTools.status]();

	return (
		<NovaInsertLibrary
			dataCy="nova-mcp-tools"
			title={formatMessage({id: 'nova.mcpTools.title'})}
			helper={formatMessage({id: 'nova.mcpTools.hint'})}
			countLabel={countLabel}
			targetLabel={targetLabel}
			groups={groups}
			notice={mcpTools.status === 'failed' ? formatMessage({id: 'nova.mcpTools.loadFailed'}) : undefined}
			noticeAction={onRetry ? {label: formatMessage({id: 'nova.insertLibrary.retry'}), onPress: onRetry} : undefined}
			emptyText={mcpTools.status === 'loading' ? formatMessage({id: 'nova.mcpTools.loading'}) : undefined}
		/>
	);
}
